import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  Select,
  SelectProps,
} from '@everlywell/ui-kit';
import { CaretDown } from '@phosphor-icons/react';
import { get, isNil } from 'lodash-es';
import React, { forwardRef, useEffect } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import { mergeRefs } from 'utils/react';

export type SelectFieldProps<T> = SelectProps & {
  label?: string;
  isFloatingLabel?: boolean;
  helperText?: string;
  name: string;
  validations?: RegisterOptions;

  // select options
  placeholder?: string;

  options: T[] | ReadonlyArray<T>;
  renderOption: (option: T | Readonly<T>) => JSX.Element;
};

/**
 * Base Input Field.
 * This component needs to be wrapped in a `FormProvider` component, since it uses `useFormContext`.
 */
function SelectField<T>(
  props: SelectFieldProps<T>,
  ref: React.ForwardedRef<HTMLSelectElement>,
): JSX.Element {
  const {
    label,
    helperText,
    name,
    placeholder,
    validations,
    options,
    renderOption,
    isFloatingLabel = false,
    ...rest
  } = props;
  const { register, errors, control } = useFormContext();

  useEffect(() => {
    // Chakra UI prevents setting DOM placeholder on Select component since they have their own approach
    // and the ts-ignore is needed because react-hook-form does not have the proper typing for this even though it's there
    // @ts-ignore
    control.fieldsRef.current[name]?.ref.setAttribute(
      'placeholder',
      placeholder,
    );
  }, [control.fieldsRef, name, placeholder]);

  const errorMessage = get(errors, name)?.message; // account for nested fields, ie "name.first"
  const hasError = Boolean(errorMessage);
  const isRequired = Boolean(validations?.required);

  return (
    <FormControl
      isInvalid={hasError}
      isRequired={isRequired}
      variant={isFloatingLabel ? 'floating' : 'default'}
    >
      {label && !isFloatingLabel ? <FormLabel>{label}</FormLabel> : null}

      <Select
        icon={<Icon as={CaretDown} />}
        ref={mergeRefs(ref, register(validations))}
        name={name}
        defaultValue=""
        fontWeight="medium"
        // TODO: Remove this once the design system is updated
        _focus={{
          borderColor: 'viridian.base',
        }}
        _focusVisible={{
          outline: 'none',
        }}
        required={false}
        placeholder={isFloatingLabel ? placeholder ?? '' : undefined}
        {...rest}
      >
        {!isNil(placeholder) && !isFloatingLabel ? (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        ) : null}
        {options.map(renderOption)}
      </Select>

      {/* floating label functionality requires the label to be placed after the select input */}
      {label && isFloatingLabel ? <FormLabel>{label}</FormLabel> : null}

      {!hasError && helperText ? (
        <FormHelperText>{helperText}</FormHelperText>
      ) : null}

      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
}

export default forwardRef(SelectField) as <T>(
  props: SelectFieldProps<T> & { ref?: React.Ref<HTMLSelectElement> },
) => JSX.Element;
